import React, { useEffect, useMemo } from 'react';
import styles from './UIGamePlayerCardInventory.module.scss';
import clsx from 'clsx';
import { CARD_IMAGES, CARD_POPUP_BODY, CARD_TIP_TITLE, cardEnumsToCardState } from '../../utils/cards.ts';
import { UIGameCardInventory } from '../../common/UIGameCardInventory/UIGameCardInventory.tsx';
import { useGameStore } from '../../store/GameStoreContext.ts';
import { CardEnum } from '@colonist/game-states';
import { ArrayUtils } from '@colonist/utils';
import { TradeErrorHighlightLocationEnum, Point } from '@colonist/game-mechanic-validators';
import { getCardsToGreyForBankTrade, removeCardsSelected, getCanClickCardDataForAllCards } from './utils.ts';
import { usePlayerValidators } from '../../store/selectors/usePlayerValidators.ts';
import { useIsReplay } from '../../store/selectors/useIsReplay.ts';
import { GameAction, ModeSettingEnum } from '@colonist/game-base-types';
import { CardClickPopupType, CardDataController, isDevelopmentCard } from '@colonist/game-card-types';
import { useCurrentStateValidator } from '../../store/selectors/useCurrentStateValidator.ts';
import { useMechanicValidatorDevelopmentCard } from '../../store/selectors/useMechanicValidatorDevelopmentCard.ts';
/**
 * Gets the HTML position of a card in the player's card inventory
 * @param cardEnum - The card to find the position for
 * @returns Point with coordinates relative to the ui-game container, or undefined if not found
 */ export function getPointPositionFromPlayerInventory(cardEnum) {
    const playerInventory = document.getElementById('player-card-inventory');
    if (!playerInventory) return undefined;
    const uiGameContainer = document.getElementById('ui-game');
    if (!uiGameContainer) return undefined;
    const uiGameRect = uiGameContainer.getBoundingClientRect();
    const cardElement = playerInventory.querySelector(`[data-card-enum="${cardEnum}"]`);
    if (!cardElement) {
        const inventoryRect = playerInventory.getBoundingClientRect();
        const inventoryCenterX = inventoryRect.left - uiGameRect.left;
        const inventoryTopY = inventoryRect.top - uiGameRect.top;
        return new Point(inventoryCenterX, inventoryTopY);
    }
    const cardRect = cardElement.getBoundingClientRect();
    const cardCenterX = cardRect.left + cardRect.width / 2 - uiGameRect.left;
    const cardTopY = cardRect.top - uiGameRect.top;
    return new Point(cardCenterX, cardTopY);
}
/**
 * Gets the dimensions of a card from the player's card inventory
 * @returns Object with width and height of a card
 */ export function getCardDimensionsFromPlayerInventory() {
    const playerInventory = document.getElementById('player-card-inventory');
    if (!playerInventory) return {
        width: 0,
        height: 0
    };
    const cardElement = playerInventory.querySelector('[data-card-enum]');
    if (!cardElement) return {
        width: 0,
        height: 0
    };
    const cardRect = cardElement.getBoundingClientRect();
    return {
        width: cardRect.width,
        height: cardRect.height
    };
}
export const UIGamePlayerCardInventory = ({ className, background = 'beige', disableTooltip = false, disableCardScaleOnHover = false })=>{
    var _gameState_mechanicDevelopmentCardsState_players_myColor, _gameState_mechanicDevelopmentCardsState_players, _gameState_mechanicDevelopmentCardsState, _gameState_mechanicProgressCardsState_players_myColor, _gameState_mechanicProgressCardsState_players, _gameState_mechanicProgressCardsState;
    const setGameCardInventory = useGameStore((state)=>state.gameCardInventory.setCardInventory);
    const selectedCards = useGameStore((state)=>state.gameCardInventory.selectedCards);
    const cardsAnimating = useGameStore((state)=>state.gameCardAnimator.cardsAnimating);
    const cardsAnimatingToMyHand = useMemo(()=>cardsAnimating.filter((card)=>card.isAnimatingToMyHand).map((card)=>card.card.cardEnum), [
        cardsAnimating
    ]);
    const cardsToDisableWhenPickingForActionBox = useGameStore((state)=>state.gameCardInventory.handCardsToDisable);
    const onSelectCardForTrade = useGameStore((state)=>state.gameCardInventory.onSelectCardForTrade);
    const onSelectCardForActionBox = useGameStore((state)=>state.gameCardInventory.onSelectCardForActionBox);
    const actionBoxType = useGameStore((state)=>{
        var _state_actionBox_actionBoxData;
        return (_state_actionBox_actionBoxData = state.actionBox.actionBoxData) === null || _state_actionBox_actionBoxData === void 0 ? void 0 : _state_actionBox_actionBoxData.type;
    });
    const isPickActionBoxOpen = useGameStore((state)=>{
        var _state_actionBox_actionBoxData;
        const type = (_state_actionBox_actionBoxData = state.actionBox.actionBoxData) === null || _state_actionBox_actionBoxData === void 0 ? void 0 : _state_actionBox_actionBoxData.type;
        return type === 'pickCard' || type === 'pickPlayer' || type === 'pickDice';
    });
    const setActionBoxData = useGameStore((state)=>state.actionBox.setActionBoxData);
    const hideActionBox = useGameStore((state)=>state.actionBox.hideActionBox);
    const gameState = useGameStore((state)=>state.gameState);
    const { getPlayerValidator } = usePlayerValidators();
    const myColor = useGameStore((state)=>state.myColor);
    const isBankTradeMode = useGameStore((state)=>state.gameTrade.isBankTradeMode);
    const isShowTradePanel = useGameStore((state)=>state.gameTrade.isShowTradePanel);
    const tradeError = useGameStore((state)=>state.gameTrade.tradeError);
    const translationKeyContext = useGameStore((state)=>state.gameMode.translationKeyContext);
    const beginnerActionState = useGameStore((state)=>state.gameBeginnerHint.beginnerActionState);
    const beginnerHintData = useGameStore((state)=>state.gameBeginnerHint.beginnerHintData);
    const modeSetting = useGameStore((state)=>state.gameSettings.modeSetting);
    const isReplay = useIsReplay();
    const { sendClickedDevelopmentCard, sendRequestActionSwap, sendConfirmUseDevelopmentCard } = useGameStore((state)=>state.gameSendEvent);
    const currentStateValidator = useCurrentStateValidator();
    const mechanicValidatorDevelopmentCard = useMechanicValidatorDevelopmentCard();
    const isBeginnerMode = modeSetting == ModeSettingEnum.BeginnerModeClassic4P;
    const offeredResourcesSuggestion = (beginnerHintData === null || beginnerHintData === void 0 ? void 0 : beginnerHintData.data.offeredResources) != null ? beginnerHintData === null || beginnerHintData === void 0 ? void 0 : beginnerHintData.data.offeredResources : [];
    const shouldShowSuggestion = offeredResourcesSuggestion.length > 0 && isShowTradePanel;
    const myPlayerValidator = getPlayerValidator(myColor);
    const myBankTradeRatioState = myPlayerValidator.bankTradeRatiosState;
    const isActionBoxPickCardOpening = actionBoxType === 'pickCard';
    const myResourceCards = myPlayerValidator.resourceCards.cards;
    var _gameState_mechanicDevelopmentCardsState_players_myColor_developmentCards_cards;
    const myDevelopmentCards = (_gameState_mechanicDevelopmentCardsState_players_myColor_developmentCards_cards = (_gameState_mechanicDevelopmentCardsState = gameState.mechanicDevelopmentCardsState) === null || _gameState_mechanicDevelopmentCardsState === void 0 ? void 0 : (_gameState_mechanicDevelopmentCardsState_players = _gameState_mechanicDevelopmentCardsState.players) === null || _gameState_mechanicDevelopmentCardsState_players === void 0 ? void 0 : (_gameState_mechanicDevelopmentCardsState_players_myColor = _gameState_mechanicDevelopmentCardsState_players[myColor]) === null || _gameState_mechanicDevelopmentCardsState_players_myColor === void 0 ? void 0 : _gameState_mechanicDevelopmentCardsState_players_myColor.developmentCards.cards) !== null && _gameState_mechanicDevelopmentCardsState_players_myColor_developmentCards_cards !== void 0 ? _gameState_mechanicDevelopmentCardsState_players_myColor_developmentCards_cards : [];
    var _gameState_mechanicProgressCardsState_players_myColor_progressCards_cards;
    const myProgressCards = (_gameState_mechanicProgressCardsState_players_myColor_progressCards_cards = (_gameState_mechanicProgressCardsState = gameState.mechanicProgressCardsState) === null || _gameState_mechanicProgressCardsState === void 0 ? void 0 : (_gameState_mechanicProgressCardsState_players = _gameState_mechanicProgressCardsState.players) === null || _gameState_mechanicProgressCardsState_players === void 0 ? void 0 : (_gameState_mechanicProgressCardsState_players_myColor = _gameState_mechanicProgressCardsState_players[myColor]) === null || _gameState_mechanicProgressCardsState_players_myColor === void 0 ? void 0 : _gameState_mechanicProgressCardsState_players_myColor.progressCards.cards) !== null && _gameState_mechanicProgressCardsState_players_myColor_progressCards_cards !== void 0 ? _gameState_mechanicProgressCardsState_players_myColor_progressCards_cards : [];
    const anyResourceCards = isShowTradePanel && !isBankTradeMode && !isBeginnerMode ? [
        CardEnum.AnyResource
    ] : [];
    const allCardsInInventory = useMemo(()=>[
            ...myResourceCards,
            ...myDevelopmentCards,
            ...myProgressCards,
            ...anyResourceCards
        ], [
        myResourceCards,
        myDevelopmentCards,
        myProgressCards,
        anyResourceCards
    ]);
    const allVisibleCards = useMemo(()=>{
        const cardsToRemove = [
            ...cardsAnimatingToMyHand,
            ...selectedCards
        ];
        return removeCardsSelected(allCardsInInventory, cardsToRemove);
    }, [
        allCardsInInventory,
        cardsAnimatingToMyHand,
        selectedCards
    ]);
    useEffect(()=>{
        setGameCardInventory(allCardsInInventory);
    }, [
        allCardsInInventory
    ]);
    const cardState = useMemo(()=>cardEnumsToCardState(allVisibleCards), [
        allVisibleCards
    ]);
    const canClickCardDataForAllCards = useMemo(()=>getCanClickCardDataForAllCards({
            allMyCards: allVisibleCards,
            currentState: gameState.currentState,
            isReplay: isReplay,
            myColor: myColor,
            translationKeyContext: translationKeyContext,
            isShowTradePanel: isShowTradePanel,
            isActionBoxPickCardOpening: isActionBoxPickCardOpening,
            currentStateValidator: currentStateValidator
        }), [
        allVisibleCards,
        gameState.currentState,
        isReplay,
        myColor,
        translationKeyContext,
        isShowTradePanel,
        isActionBoxPickCardOpening,
        currentStateValidator
    ]);
    const canPlayDevelopmentCard = (cardEnum)=>{
        if (!isDevelopmentCard(cardEnum)) return true;
        if (!mechanicValidatorDevelopmentCard) return true;
        const canClickCardData = canClickCardDataForAllCards[cardEnum];
        if (!(canClickCardData === null || canClickCardData === void 0 ? void 0 : canClickCardData.canClickCard)) return false;
        const canUseDevelopmentCard = mechanicValidatorDevelopmentCard.canPlayerUseDevelopmentCard(myColor, cardEnum);
        if (!canUseDevelopmentCard.canTakeAction) return false;
        return true;
    };
    const unplayableDevelopmentCards = useMemo(()=>{
        if (!mechanicValidatorDevelopmentCard || isShowTradePanel) return [];
        const unplayableCards = [];
        for (const devCard of myDevelopmentCards){
            if (!canPlayDevelopmentCard(devCard)) {
                unplayableCards.push(devCard);
            }
        }
        return unplayableCards;
    }, [
        mechanicValidatorDevelopmentCard,
        myDevelopmentCards,
        myColor,
        isShowTradePanel,
        canClickCardDataForAllCards
    ]);
    const cardsToDisable = useMemo(()=>{
        if (cardsToDisableWhenPickingForActionBox != undefined && isActionBoxPickCardOpening) return cardsToDisableWhenPickingForActionBox;
        let cardsToGrey = [];
        if (isShowTradePanel) {
            cardsToGrey = [
                ...myDevelopmentCards,
                ...myProgressCards
            ];
        }
        if (isShowTradePanel && isBankTradeMode) {
            cardsToGrey.push(...getCardsToGreyForBankTrade(myBankTradeRatioState, cardState));
        }
        if (!isShowTradePanel) {
            cardsToGrey.push(...unplayableDevelopmentCards);
        }
        return cardEnumsToCardState(cardsToGrey);
    }, [
        myDevelopmentCards,
        myProgressCards,
        myBankTradeRatioState,
        cardsToDisableWhenPickingForActionBox,
        isShowTradePanel,
        isBankTradeMode,
        cardState,
        isActionBoxPickCardOpening,
        unplayableDevelopmentCards
    ]);
    const cardsToHighlight = useMemo(()=>{
        var _tradeError_payload_highlightCards, _tradeError_payload, _tradeError_payload_highlightCards1, _tradeError_payload1;
        var _tradeError_payload_highlightCards_cardsToHighlight;
        const tradeErrorHighlightCards = (_tradeError_payload_highlightCards_cardsToHighlight = tradeError === null || tradeError === void 0 ? void 0 : (_tradeError_payload = tradeError.payload) === null || _tradeError_payload === void 0 ? void 0 : (_tradeError_payload_highlightCards = _tradeError_payload.highlightCards) === null || _tradeError_payload_highlightCards === void 0 ? void 0 : _tradeError_payload_highlightCards.cardsToHighlight) !== null && _tradeError_payload_highlightCards_cardsToHighlight !== void 0 ? _tradeError_payload_highlightCards_cardsToHighlight : [];
        var _tradeError_payload_highlightCards_locations;
        const tradeErrorHighlightLocations = (_tradeError_payload_highlightCards_locations = tradeError === null || tradeError === void 0 ? void 0 : (_tradeError_payload1 = tradeError.payload) === null || _tradeError_payload1 === void 0 ? void 0 : (_tradeError_payload_highlightCards1 = _tradeError_payload1.highlightCards) === null || _tradeError_payload_highlightCards1 === void 0 ? void 0 : _tradeError_payload_highlightCards1.locations) !== null && _tradeError_payload_highlightCards_locations !== void 0 ? _tradeError_payload_highlightCards_locations : [];
        if (tradeErrorHighlightLocations.includes(TradeErrorHighlightLocationEnum.OfferedSelector)) {
            return tradeErrorHighlightCards;
        }
        return [];
    }, [
        tradeError
    ]);
    const cardsToScaleOnHover = useMemo(()=>{
        if (isShowTradePanel || disableCardScaleOnHover) return [];
        const cardsToScale = [
            ...myProgressCards
        ];
        ArrayUtils.getUniqueElements(cardsToScale);
        return cardsToScale;
    }, [
        myProgressCards,
        isShowTradePanel,
        disableCardScaleOnHover
    ]);
    const cardsHelperText = useMemo(()=>{
        const cardsHelperTextLocal = {};
        for(const cardEnum in canClickCardDataForAllCards){
            cardsHelperTextLocal[cardEnum] = canClickCardDataForAllCards[cardEnum].helperText;
        }
        if (mechanicValidatorDevelopmentCard) {
            for (const devCard of myDevelopmentCards){
                var _canClickCardDataForAllCards_devCard;
                if (((_canClickCardDataForAllCards_devCard = canClickCardDataForAllCards[devCard]) === null || _canClickCardDataForAllCards_devCard === void 0 ? void 0 : _canClickCardDataForAllCards_devCard.blockedReason) === 'notYourTurn') continue;
                const canUseDevelopmentCard = mechanicValidatorDevelopmentCard.canPlayerUseDevelopmentCard(myColor, devCard);
                if (!canUseDevelopmentCard.canTakeAction && canUseDevelopmentCard.errorMessageIfCantTakeAction) {
                    cardsHelperTextLocal[devCard] = canUseDevelopmentCard.errorMessageIfCantTakeAction;
                }
            }
        }
        return cardsHelperTextLocal;
    }, [
        canClickCardDataForAllCards,
        mechanicValidatorDevelopmentCard,
        myDevelopmentCards,
        myColor
    ]);
    const cardsToShowTooltip = useMemo(()=>{
        if (!isActionBoxPickCardOpening) return undefined;
        const progressCardsNotGreyedOut = myProgressCards.filter((cardEnum)=>{
            var _cardsToDisable_cardEnum;
            const greyedOutCount = (_cardsToDisable_cardEnum = cardsToDisable[cardEnum]) !== null && _cardsToDisable_cardEnum !== void 0 ? _cardsToDisable_cardEnum : 0;
            var _cardState_cardEnum;
            const totalCount = (_cardState_cardEnum = cardState[cardEnum]) !== null && _cardState_cardEnum !== void 0 ? _cardState_cardEnum : 0;
            return greyedOutCount < totalCount;
        });
        return progressCardsNotGreyedOut;
    }, [
        isActionBoxPickCardOpening,
        myProgressCards,
        cardsToDisable,
        cardState
    ]);
    const handleCreateCardConfirmationPopup = (cardEnum)=>{
        sendRequestActionSwap(GameAction.ClickedDevelopmentCard);
        const popupInformation = {
            cardEnum: cardEnum,
            cardBody: CARD_POPUP_BODY[cardEnum]
        };
        sendConfirmUseDevelopmentCard(popupInformation);
    };
    const showCardInfoPopup = (cardEnum)=>{
        setActionBoxData({
            id: Date.now(),
            type: 'base',
            props: {
                title: CARD_TIP_TITLE[cardEnum],
                body: CARD_POPUP_BODY[cardEnum],
                imagePath: CARD_IMAGES[cardEnum],
                isInfoOnly: true,
                onClose: hideActionBox
            }
        });
    };
    const handleClickCard = (cardEnum)=>{
        const canClickCardData = canClickCardDataForAllCards[cardEnum];
        if ((canClickCardData === null || canClickCardData === void 0 ? void 0 : canClickCardData.canClickCard) !== true) {
            if ((canClickCardData === null || canClickCardData === void 0 ? void 0 : canClickCardData.blockedReason) === 'notYourTurn' && !isPickActionBoxOpen) {
                if (CardDataController.isDevelopmentOrProgressCard(cardEnum)) {
                    showCardInfoPopup(cardEnum);
                }
            }
            return;
        }
        const isNotBeginnerGameActionClickedDevCard = beginnerActionState != null && (beginnerActionState === null || beginnerActionState === void 0 ? void 0 : beginnerActionState.type) != GameAction.ClickedDevelopmentCard;
        const isNotBeginnerGameActionTrade = beginnerActionState != null && (beginnerActionState === null || beginnerActionState === void 0 ? void 0 : beginnerActionState.type) != GameAction.CreateTrade;
        if (actionBoxType === 'pickCard') {
            onSelectCardForActionBox === null || onSelectCardForActionBox === void 0 ? void 0 : onSelectCardForActionBox(cardEnum);
            return;
        }
        if (isShowTradePanel) {
            if (isNotBeginnerGameActionTrade) {
                return;
            }
            onSelectCardForTrade === null || onSelectCardForTrade === void 0 ? void 0 : onSelectCardForTrade(cardEnum);
            return;
        }
        if (!canPlayDevelopmentCard(cardEnum)) return;
        const cardData = CardDataController.getCardDataForCard(cardEnum);
        if ((cardData === null || cardData === void 0 ? void 0 : cardData.clickPopupType) == CardClickPopupType.NormalClick) {
            if (isNotBeginnerGameActionClickedDevCard) {
                return;
            }
            sendClickedDevelopmentCard(cardEnum);
            return;
        }
        if ((cardData === null || cardData === void 0 ? void 0 : cardData.clickPopupType) == CardClickPopupType.TradeWindow) {
            if (isNotBeginnerGameActionTrade) {
                return;
            }
            onSelectCardForTrade === null || onSelectCardForTrade === void 0 ? void 0 : onSelectCardForTrade(cardEnum);
            return;
        }
        if ((cardData === null || cardData === void 0 ? void 0 : cardData.clickPopupType) == CardClickPopupType.ConfirmationPopup) {
            if (isNotBeginnerGameActionClickedDevCard) {
                return;
            }
            handleCreateCardConfirmationPopup(cardEnum);
            return;
        }
    };
    const shouldShowBankTradeRatio = isBankTradeMode && isShowTradePanel;
    const tooltipEnabled = !disableTooltip && !isShowTradePanel;
    return /*#__PURE__*/ React.createElement("div", {
        className: clsx(styles.container, className, styles[background])
    }, /*#__PURE__*/ React.createElement(UIGameCardInventory, {
        onClickCard: handleClickCard,
        cardState: cardState,
        className: styles.inventory,
        showTipTitle: tooltipEnabled,
        showTipBody: tooltipEnabled,
        cardsToDisable: cardsToDisable,
        cardsToHighlight: cardsToHighlight,
        cardsToSuggestion: shouldShowSuggestion ? offeredResourcesSuggestion : [],
        cardsToScaleOnHover: cardsToScaleOnHover,
        cardsHelperText: cardsHelperText,
        bankTradeRatiosState: shouldShowBankTradeRatio ? myBankTradeRatioState : undefined,
        cardsToShowTooltip: cardsToShowTooltip,
        id: "player-card-inventory"
    }));
};
